import { Product } from '@features/accounts/types';
import { getProfileQueryOptions } from '@features/user/api/get-profile';
import { api } from '@src/lib/api-client';
import { queryClient, QueryConfig } from '@src/lib/react-query';
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { ToastUtility } from '@utils';
import { AxiosResponse } from 'axios';
import { useEffect, useRef } from 'react';

export type GetProductsResponse = {
  savings: Product[];
  checking: Product[];
};

export async function getProducts(): Promise<GetProductsResponse> {
  return await api
    .get(`/api/v3/user/products`)
    .then((res: AxiosResponse<GetProductsResponse>) => res.data);
}

export const getProductsQueryOptions = queryOptions({
  queryKey: ['products', 'list'],
  queryFn: getProducts,
  refetchInterval: (query) => {
    const data = query.state.data;
    const hasNewlyPending = data?.savings?.some((p) => p.newlyPending) ?? false;
    return hasNewlyPending ? 5000 : false; // 5s or stop
  },
  refetchIntervalInBackground: true,
});

// Helper to count newly pending items
const countNewlyPending = (data: GetProductsResponse | undefined) =>
  data?.savings?.reduce((acc, p) => acc + (p.newlyPending ? 1 : 0), 0) ?? 0;

type UseGetProductsOptions = {
  queryConfig?: QueryConfig<typeof getProductsQueryOptions>;
};

export const useGetProducts = ({ queryConfig }: UseGetProductsOptions = {}) => {
  const query = useSuspenseQuery({
    ...getProductsQueryOptions,
    ...queryConfig,
  });

  // Track previous count without causing re-renders
  const prevCountRef = useRef<number | null>(null);

  useEffect(() => {
    const currentCount = countNewlyPending(query.data);
    const prev = prevCountRef.current;
    // A decrease in newlyPending indicates an approval landed, so refresh
    // dependent caches that drive account and optimization UI.
    if (prev !== null && prev > currentCount) {
      void queryClient.invalidateQueries({
        queryKey: getProfileQueryOptions.queryKey,
      });

      if (!queryClient.isFetching({ queryKey: ['accounts'] })) {
        void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      }
    }

    prevCountRef.current = currentCount;
  }, [query.data]); // runs on each data change from polling/refetch

  return query;
};

export const callGetProductsErrorToast = () => {
  ToastUtility.error('Failed to fetch Products', { toastId: 'products' });
};
